<template>
    <v-dialog :value="value" max-width="1150px" persistent scrollable @input="$emit('input', $event)">
        <v-card class="booking-dialog-card">
            <div class="booking-dialog-header pa-5">
                <div class="d-flex align-center justify-space-between">
                    <div>
                        <div class="booking-dialog-title">
                            <v-icon class="mr-2" color="white" size="20">mdi-calendar-question</v-icon>
                            Request Availability
                        </div>
                        <div class="booking-dialog-subtitle mt-1">
                            Ask one supplier, or every supplier in a category at {{ locationName }}, whether they can take
                            this group. Each gets its own Available / Not Available link.
                        </div>
                    </div>
                    <v-btn icon dark :disabled="sending" @click="close()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>

            <v-card-text class="pa-6">
                <v-row dense>
                    <v-col cols="4">
                        <div class="booking-section-header mb-1">CATEGORY</div>
                        <v-select v-model="supplier_type_id" :items="typeItems" item-text="name" item-value="id"
                            outlined dense hide-details :disabled="sending" @change="showAll()" />

                        <div class="booking-section-header mb-1 mt-3">DATE ASKED ABOUT</div>
                        <v-text-field v-model="requested_date" type="date" outlined dense hide-details :disabled="sending" />

                        <div class="booking-section-header mb-2 mt-4">
                            <v-checkbox :input-value="allSelected" :indeterminate="someSelected && !allSelected"
                                :disabled="sending || selectable.length === 0" hide-details dense class="mt-0 pt-0 mr-1"
                                @click="toggleAll()" />
                            SUPPLIERS ({{ selectedRows.length }} of {{ rows.length }} selected)
                        </div>
                        <v-text-field v-if="!only" v-model="search" placeholder="Search by name or email" prepend-inner-icon="mdi-magnify"
                            outlined dense hide-details clearable class="mb-2" />
                        <div v-else class="supplier-list-meta mb-2">
                            Only {{ rows.length === 1 ? 'this supplier' : 'these suppliers' }}.
                            <a href="#" :class="{ 'grey--text': sending }" @click.prevent="!sending && showAll()">
                                Show every supplier at {{ locationName }}
                            </a>
                        </div>
                        <v-progress-linear v-if="loading" indeterminate color="#3988AA" />
                        <div class="supplier-list">
                            <div v-if="!loading && rows.length === 0" class="pa-4 supplier-list-meta">
                                No suppliers in this category at {{ locationName }}.
                            </div>
                            <div v-else-if="!loading && visibleRows.length === 0" class="pa-4 supplier-list-meta">
                                No suppliers match "{{ search }}".
                            </div>
                            <div v-for="row in visibleRows" :key="row.id" class="supplier-list-item"
                                @click="canSelect(row) && (row.selected = !row.selected)">
                                <v-checkbox v-model="row.selected" :disabled="!canSelect(row)" hide-details dense
                                    class="mt-0 pt-0" @click.stop />
                                <div class="supplier-list-text" style="flex:1;">
                                    <div class="supplier-list-name">{{ row.supplier_name }}</div>
                                    <div class="supplier-list-meta">{{ row.email || 'No email address' }}</div>
                                    <span v-if="row.last_status" class="supplier-list-tag"
                                        :style="{ background: status[row.last_status].color, color: status[row.last_status].text }">
                                        Asked: {{ status[row.last_status].label }}
                                    </span>
                                    <span v-if="blockedNote(row)" class="supplier-list-tag supplier-list-tag--failed">
                                        {{ blockedNote(row) }}
                                    </span>
                                    <span v-if="row.result === 'sent'" class="supplier-list-tag supplier-list-tag--sent">
                                        <v-icon x-small color="#27AE60" class="mr-1">mdi-check</v-icon>Sent
                                    </span>
                                    <span v-if="row.result === 'failed'" class="supplier-list-tag supplier-list-tag--failed">
                                        {{ row.error || 'Failed to send' }}
                                    </span>
                                    <!-- Optional: the products to ask about; none ticked asks about the supplier in general -->
                                    <div v-if="row.selected && row.products && row.products.length" class="supplier-products" @click.stop>
                                        <div class="supplier-products-label">Products to ask about (optional)</div>
                                        <v-checkbox v-for="product in row.products" :key="product.id" v-model="row.product_ids"
                                            :value="product.id" :label="product.name" :disabled="sending" hide-details dense
                                            class="mt-0 pt-0 supplier-product-check" />
                                    </div>
                                    <div v-else-if="!row.selected && row.product_ids.length" class="supplier-list-meta">
                                        {{ productNames(row) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-col>

                    <v-col cols="8">
                        <div class="booking-section-header mb-1">TEMPLATE</div>
                        <v-select v-model="template_id" :items="templateItems" item-text="subject" item-value="id"
                            outlined dense hide-details :disabled="sending" @change="applyTemplate()" />

                        <div class="booking-section-header mb-1 mt-4">SUBJECT</div>
                        <v-text-field v-model="subject" outlined dense hide-details :disabled="sending || previewing" />

                        <div class="booking-section-header mb-2 mt-4">
                            EMAIL CONTENT
                            <span v-if="single" class="booking-optional ml-1">(exactly what {{ single.supplier_name }} will get)</span>
                            <span v-else class="booking-optional ml-1">(the same email goes to every supplier picked)</span>
                        </div>
                        <v-progress-linear v-if="previewing" indeterminate color="#3988AA" />
                        <wysiwyg :key="editor_version" v-model="body" placeholder="Compose the availability request..." />
                        <div v-if="single" class="dialog-note mt-3">
                            Edit anything before sending. Changing the supplier, products, date or template rebuilds the email.
                            Pick more than one supplier to edit the template with its {placeholders} instead.
                        </div>
                        <div v-else class="dialog-note mt-3">
                            Filled in per supplier when sent: {{ placeholders.join(' ') }}.
                            If the email has no {available_link} / {unavailable_link}, the buttons are added at the end.
                            Pick one supplier to see exactly what they will get.
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider />
            <v-card-actions class="pa-4 custom-buttons">
                <v-spacer />
                <v-btn text :disabled="sending" @click="close()">{{ anySent ? 'Close' : 'Cancel' }}</v-btn>
                <v-btn color="#3988AA" :dark="canSend" :disabled="!canSend" :loading="sending || previewing" @click="send()">
                    <v-icon small left>mdi-send</v-icon>
                    Send to {{ selectedRows.length }} supplier{{ selectedRows.length === 1 ? '' : 's' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import {
    AVAILABILITY_PLACEHOLDERS, DEFAULT_AVAILABILITY_BODY, DEFAULT_AVAILABILITY_SUBJECT, isAvailabilityTemplate, STATUS,
} from '../../../utils/supplierAvailability'
import { unavailableBetween, unavailableLabel } from '../../../utils/supplierUnavailability'
import { serviceOption, servicesHtml } from '@/utils/supplierTemplateEmails'

export default {
    props: {
        value: { type: Boolean, default: false },
        lead: { type: Object, required: true },
        types: { type: Array, default: () => [] },
        // Open with these pre-set (e.g. "Ask again", or an event's suppliers and their booked products).
        // Preset suppliers are listed even when they aren't at the lead's location.
        presetTypeId: { type: Number, default: null },
        presetSupplierIds: { type: Array, default: () => [] },
        presetProductIds: { type: Array, default: () => [] },
        // List only the preset suppliers (Email Supplier menu, Ask Again): opens at once instead of
        // loading every supplier at the location. "Show every supplier" loads the rest.
        onlyPreset: { type: Boolean, default: false },
        // The event's booked services (quote builds), for {services}: date/time, rate and groupsize.
        builds: { type: Array, default: () => [] },
        locationName: { type: String, default: 'this location' },
    },
    data: () => ({
        supplier_type_id: null,
        requested_date: null,
        rows: [],
        search: '',
        only: false,
        loaded_types: [],
        loading: false,
        templates: [],
        template_id: 0,
        // What's in the editor: the template (with placeholders) for several suppliers, or one
        // supplier's filled-in email (preview, with its link token) when only one is ticked.
        subject: DEFAULT_AVAILABILITY_SUBJECT,
        body: DEFAULT_AVAILABILITY_BODY,
        template_subject: DEFAULT_AVAILABILITY_SUBJECT,
        template_body: DEFAULT_AVAILABILITY_BODY,
        preview_token: null,
        previewing: false,
        editor_version: 0,
        sending: false,
        anySent: false,
        status: STATUS,
        placeholders: AVAILABILITY_PLACEHOLDERS,
    }),
    computed: {
        ...mapGetters({ get_selected_company: 'auth/get_selected_company' }),
        typeItems() {
            return [{ id: null, name: 'All categories' }, ...(this.types.length ? this.types : this.loaded_types)]
        },
        templateItems() {
            return [{ id: 0, subject: 'Default availability request' }, ...this.templates]
        },
        // Search filters the list; ticked suppliers stay ticked when hidden.
        visibleRows() {
            const term = (this.search || '').trim().toLowerCase()
            if (!term) return this.rows
            return this.rows.filter(r => (r.supplier_name || '').toLowerCase().includes(term)
                || (r.email || '').toLowerCase().includes(term))
        },
        selectable() {
            return this.visibleRows.filter(this.canSelect)
        },
        selectedRows() {
            return this.rows.filter(r => r.selected)
        },
        allSelected() {
            return this.selectable.length > 0 && this.selectable.every(r => r.selected)
        },
        someSelected() {
            return this.selectedRows.length > 0
        },
        canSend() {
            return !this.sending && !this.previewing && this.selectedRows.length > 0 && !!this.subject && !!this.body
        },
        single() {
            return this.selectedRows.length === 1 ? this.selectedRows[0] : null
        },
        // What the one supplier's email depends on; a change rebuilds it.
        previewKey() {
            const row = this.single
            return row ? JSON.stringify([row.id, row.product_ids, this.requested_date, this.supplier_type_id, this.template_id]) : null
        },
    },
    watch: {
        previewKey(key, old) {
            if (key) {
                // Moving from the template to one supplier: keep the template edits to come back to.
                if (!old) this.saveTemplate()
                this.loadPreview()
            } else if (old) {
                this.setEditor(this.template_subject, this.template_body)
                this.preview_token = null
            }
        },
        value: {
            immediate: true,
            handler(open) {
                if (open) this.reset()
            },
        },
    },
    methods: {
        async reset() {
            this.supplier_type_id = this.presetTypeId
            this.search = ''
            this.only = this.onlyPreset && this.presetSupplierIds.length > 0
            this.rows = []
            this.requested_date = this.lead.start_date ? String(this.lead.start_date).substring(0, 10) : null
            this.anySent = false
            this.template_id = 0
            this.template_subject = DEFAULT_AVAILABILITY_SUBJECT
            this.template_body = DEFAULT_AVAILABILITY_BODY
            this.preview_token = null
            this.setEditor(DEFAULT_AVAILABILITY_SUBJECT, DEFAULT_AVAILABILITY_BODY)
            this.loadTemplates()
            if (!this.types.length && !this.loaded_types.length) this.loadTypes()
            await this.loadCandidates()
            this.rows.forEach(row => {
                if (!this.presetSupplierIds.includes(row.id)) return
                if (this.canSelect(row)) row.selected = true
                row.product_ids = (row.products || []).map(p => p.id).filter(id => this.presetProductIds.includes(id))
            })
        },
        productNames(row) {
            return (row.products || []).filter(p => row.product_ids.includes(p.id)).map(p => p.name).join(', ')
        },
        // Already on the supplier's Unavailable Dates for the date asked about (still askable).
        blockedNote(row) {
            return unavailableLabel(unavailableBetween(row, this.requested_date))
        },
        canSelect(row) {
            return !this.sending && !!row.email && row.result !== 'sent'
        },
        async loadCandidates() {
            this.loading = true
            try {
                const params = { supplier_ids: this.presetSupplierIds }
                if (this.only) params.only = 1
                if (this.supplier_type_id) params.supplier_type_id = this.supplier_type_id
                const { data } = await this.$axios.get(`admin/supplier_availability/get_candidates/${this.lead.id}`, { params })
                // The preset suppliers first (sort is stable, so the rest stay by name). Suppliers
                // already in the list keep their ticks, products and send result.
                const preset = id => (this.presetSupplierIds.includes(id) ? 0 : 1)
                const before = new Map(this.rows.map(r => [r.id, r]))
                this.rows = (data.data || [])
                    .map(s => {
                        const old = before.get(s.id)
                        return old
                            ? { ...s, selected: old.selected, product_ids: old.product_ids, result: old.result, error: old.error }
                            : { ...s, selected: false, product_ids: [], result: null, error: null }
                    })
                    .sort((a, b) => preset(a.id) - preset(b.id))
            } finally {
                this.loading = false
            }
        },
        // The whole location (or category): from the "Show every supplier" link or a category change.
        showAll() {
            this.only = false
            this.loadCandidates()
        },
        // Categories, when the parent doesn't pass them (the events list).
        async loadTypes() {
            try {
                const { data } = await this.$axios.get('api/suppliers/get_supplier_type')
                this.loaded_types = data.data || []
            } catch (e) {
                this.loaded_types = []
            }
        },
        async loadTemplates() {
            try {
                const { data } = await this.$axios.get(`admin/email_templates/get_email_templates/${this.get_selected_company.id}`)
                this.templates = (data.data || []).filter(isAvailabilityTemplate)
            } catch (e) {
                this.templates = []
            }
        },
        // With one supplier ticked the previewKey watcher rebuilds the email from the new template.
        applyTemplate() {
            const template = this.templates.find(t => t.id === this.template_id)
            this.template_subject = template ? template.subject : DEFAULT_AVAILABILITY_SUBJECT
            this.template_body = template ? template.body : DEFAULT_AVAILABILITY_BODY
            if (!this.single) this.setEditor(this.template_subject, this.template_body)
        },
        saveTemplate() {
            this.template_subject = this.subject
            this.template_body = this.body
        },
        setEditor(subject, body) {
            this.subject = subject
            this.body = body
            this.editor_version++
        },
        // {services}: the supplier's booked services on the event (only the picked products', when
        // any are), as the Email Supplier templates show them. Empty off an event.
        servicesFor(row) {
            const builds = this.buildsFor(row)
            return builds.length ? servicesHtml(builds, this.get_selected_company.currency) : ''
        },
        buildsFor(row) {
            return this.builds.filter(build => build.supplier_id === row.id
                && (!row.product_ids.length || row.product_ids.includes(build.option_id)))
        },
        // The same services as plain text, for the supplier's answer page.
        serviceRowsFor(row) {
            return this.buildsFor(row).map(build => {
                const { name, date, time, rate, groupsize, nights } = serviceOption(build, this.get_selected_company.currency)
                return { name, date, time, rate, groupsize, nights: nights || [] }
            })
        },
        // One supplier's email filled in by the API exactly as it sends, buttons included.
        async loadPreview() {
            const row = this.single
            const key = this.previewKey
            this.previewing = true
            try {
                const { data } = await this.$axios.post('admin/supplier_availability/preview', {
                    lead_id: this.lead.id,
                    supplier_id: row.id,
                    supplier_type_id: this.supplier_type_id,
                    requested_date: this.requested_date,
                    product_ids: row.product_ids,
                    services: this.servicesFor(row),
                    token: this.preview_token,
                    subject: this.template_subject,
                    body: this.template_body,
                })
                if (key !== this.previewKey) return // changed again while loading
                this.preview_token = data.data.token
                this.setEditor(data.data.subject, data.data.body)
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Failed to preview the email')
            } finally {
                if (key === this.previewKey) this.previewing = false
            }
        },
        toggleAll() {
            const on = !this.allSelected
            this.selectable.forEach(r => { r.selected = on })
        },
        async send() {
            this.sending = true
            try {
                const { data } = await this.$axios.post('admin/supplier_availability/send_requests', {
                    lead_id: this.lead.id,
                    supplier_type_id: this.supplier_type_id,
                    requested_date: this.requested_date,
                    subject: this.subject,
                    body: this.body,
                    suppliers: this.selectedRows.map(r => ({
                        id: r.id,
                        product_ids: r.product_ids,
                        services: this.servicesFor(r),
                        service_rows: this.serviceRowsFor(r),
                        token: this.single ? this.preview_token : null,
                    })),
                })
                const results = data.data.results || []
                results.forEach(res => {
                    const row = this.rows.find(r => r.id === res.supplier_id)
                    if (!row) return
                    row.result = res.sent ? 'sent' : 'failed'
                    row.error = res.sent ? null : res.message
                    if (res.sent) {
                        row.selected = false
                        row.last_status = 'waiting'
                    }
                })
                const sent = results.filter(r => r.sent).length
                const failed = results.length - sent
                if (sent) {
                    this.anySent = true
                    this.$emit('sent', data.data.requests)
                }
                if (failed) this.$toast.error(`${failed} request${failed === 1 ? '' : 's'} not sent`)
                else this.$toast.success(`Availability request sent to ${sent} supplier${sent === 1 ? '' : 's'}`)
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Failed to send availability requests')
            } finally {
                this.sending = false
            }
        },
        close() {
            this.$emit('input', false)
        },
    },
}
</script>

<style lang="scss" scoped>
.booking-dialog-card {
    border-radius: 12px !important;
    overflow: hidden;
}

.booking-dialog-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.booking-dialog-title {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    color: white;
    display: flex;
    align-items: center;
}

.booking-dialog-subtitle {
    font-family: Manrope;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.85);
}

.booking-section-header {
    font-family: Manrope;
    font-size: 12px;
    font-weight: 700;
    color: #3D3D3D;
    display: flex;
    align-items: center;
}

.booking-optional {
    font-weight: 400;
    color: #9E9E9E;
}

.supplier-list {
    border: 1px solid #E9E9E9;
    border-radius: 8px;
    max-height: 45vh;
    overflow-y: auto;
}

.supplier-list-item {
    display: flex;
    align-items: flex-start;
    padding: 10px 12px;
    border-bottom: 1px solid #F0F0F0;
    cursor: pointer;
    font-family: Manrope;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: #F7FBFD;
    }
}

.supplier-list-text {
    min-width: 0;
}

.supplier-list-name {
    font-size: 14px;
    font-weight: 700;
    color: #101010;
}

.supplier-list-meta {
    font-size: 12px;
    color: #7C7C7C;
    word-break: break-all;
}

.supplier-list-tag {
    display: inline-flex;
    align-items: center;
    margin-top: 4px;
    margin-right: 4px;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    background: #FFF3E2;
    color: #B26A00;
}

.supplier-products {
    margin-top: 6px;
    padding: 6px 8px;
    background: #F7FBFD;
    border-radius: 6px;
    cursor: default;
}

.supplier-products-label {
    font-size: 11px;
    font-weight: 700;
    color: #7C7C7C;
    margin-bottom: 2px;
}

.supplier-product-check ::v-deep .v-label {
    font-size: 13px;
}

.supplier-list-tag--sent {
    background: #E6F3E2 !important;
    color: #27AE60 !important;
}

.supplier-list-tag--failed {
    background: #FFCFCF !important;
    color: #AA3939 !important;
}

.dialog-note {
    font-family: Manrope;
    font-size: 12px;
    color: #555;
    background: #F7F7F7;
    border-radius: 6px;
    padding: 8px 10px;
}
</style>
